// src/pages/Paywall.tsx
// Dynamische Angebots-Bridge — Paketempfehlung auf Basis echter Audit-Daten

import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Calendar,
  CheckCircle2,
  Clock3,
  Lock,
  ShieldAlert,
  Sparkles,
  Target,
  TrendingDown,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuditStore } from '@/hooks/useAuditStore';
import { ROUTE_PATHS } from '@/lib/index';
import {
  formatCurrency,
  getPackageRecommendation,
} from '@/lib/packageRecommendation';

// ── Konfiguration — hier anpassen ──────────────────────────────────────────
const CALENDLY_URL = 'https://calendly.com/jbhuber10';
const OFFER_EMAIL = 'mailto:kontakt@salesforge.de?subject=Salesforge%20Angebot';

// ── Urgency-Farben ──────────────────────────────────────────────────────────
const urgencyStyles = {
  hoch: 'border-red-200 bg-red-50/60',
  mittel: 'border-amber-200 bg-amber-50/60',
  niedrig: 'border-primary/20 bg-primary/5',
};

const urgencyBadge = {
  hoch: 'bg-red-100 text-red-700 border-red-200',
  mittel: 'bg-amber-100 text-amber-700 border-amber-200',
  niedrig: 'bg-primary/10 text-primary border-primary/20',
};

const urgencyLabel = {
  hoch: 'Hoher Handlungsbedarf',
  mittel: 'Mittlerer Handlungsbedarf',
  niedrig: 'Klarer nächster Schritt',
};

export default function Paywall() {
  const navigate = useNavigate();
  const location = useLocation();
  const { result } = useAuditStore();

  // ── Stripe Cancel detection: kommt von StripeRedirectHandler via navigate(state) ──
  const isCancelled = (location.state as { stripeCancelled?: boolean } | null)?.stripeCancelled === true;

  const recommendation = useMemo(() => getPackageRecommendation(result), [result]);

  // Die anderen zwei Pakete — klein als Alternative anzeigen
  const allPackages = useMemo(() => {
    const keys: Array<'diagnose' | 'optimierung' | 'transformation'> = [
      'diagnose',
      'optimierung',
      'transformation',
    ];
    return keys.filter((k) => k !== recommendation.key);
  }, [recommendation.key]);

  const packageMeta: Record<
    'diagnose' | 'optimierung' | 'transformation',
    { name: string; priceLabel: string; durationLabel: string; description: string }
  > = {
    diagnose: {
      name: 'Paket 1 · Diagnose',
      priceLabel: '1.200–2.500 €',
      durationLabel: '1–2 Wochen',
      description: 'Priorisierung der Hebel, wirtschaftliche Einordnung, 30–90-Tage-Fahrplan.',
    },
    optimierung: {
      name: 'Paket 2 · Optimierung',
      priceLabel: '3.500–6.000 €',
      durationLabel: '6–10 Wochen',
      description: 'Gezielte Hebelarbeit an Conversion, Prozessqualität und Umsatzlecks.',
    },
    transformation: {
      name: 'Paket 3 · Transformation',
      priceLabel: '7.500–14.000 €',
      durationLabel: '12–20 Wochen',
      description: 'Struktureller Umbau von Prozess, Conversion und Steuerung.',
    },
  };

  // ── Kein Audit vorhanden ────────────────────────────────────────────────
  if (!result) {
    return (
      <Layout hideFooterCta>
        <div className="flex-1 flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md px-4">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Kein Audit gefunden</h2>
            <p className="text-muted-foreground mb-6 text-sm leading-relaxed">
              Bitte führen Sie zuerst das Sales Audit durch. Die Paketempfehlung basiert
              auf Ihrem Umsatzverlust, Reifegrad und den blockierenden Funnel-Stufen.
            </p>
            <Button onClick={() => navigate(ROUTE_PATHS.AUDIT)}>
              Zum Audit
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const {
    monthlyLoss,
    annualLoss,
    scoreSignals,
    headline,
    rationale,
    reasons,
    outcome,
    priceLabel,
    durationLabel,
    ctaLabel,
    name,
    nextStep,
    urgency,
  } = recommendation;

  return (
    <Layout hideFooterCta>
      <div className="min-h-screen bg-background">

        {/* ── Abgebrochene Zahlung — Hinweis ────────────────────────────── */}
        {isCancelled && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 text-center text-sm text-amber-800">
            <strong>Zahlung abgebrochen.</strong> Kein Betrag wurde belastet. Sie können es jederzeit erneut versuchen.
          </div>
        )}

        {/* ── HERO: bg-primary wie Landingpage ──────────────────────────── */}
        <section className="relative overflow-hidden bg-primary text-primary-foreground py-14 md:py-20">
          {/* Hintergrund-Akzente */}
          <div className="pointer-events-none absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-foreground/10 via-transparent to-accent/10" />
            <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-accent/20 blur-3xl animate-pulse" />
            <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-primary-foreground/20 blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px]" />
          </div>

          <div className="container mx-auto px-4 max-w-6xl relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Badge */}
              <Badge
                variant="outline"
                className="mb-6 border-primary-foreground/30 bg-primary-foreground/10 px-4 py-2 text-primary-foreground"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Dynamische Paketempfehlung auf Basis Ihres Audits
              </Badge>

              <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
                <div>
                  {/* Urgency Badge */}
                  <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wider mb-4 ${urgencyBadge[urgency]}`}>
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-60" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-current" />
                    </span>
                    {urgencyLabel[urgency]}
                  </div>

                  <h1 className="text-3xl md:text-5xl font-black text-primary-foreground leading-tight tracking-tight max-w-3xl mb-4">
                    {monthlyLoss > 0 ? (
                      <>
                        Ihr Vertrieb verliert aktuell{' '}
                        <span className="text-accent">{formatCurrency(monthlyLoss)}</span>{' '}
                        pro Monat.
                      </>
                    ) : (
                      <>
                        Ihr Audit ist abgeschlossen.{' '}
                        <span className="text-accent">Jetzt geht es um den richtigen Hebel.</span>
                      </>
                    )}
                  </h1>
                  <p className="text-lg text-primary-foreground/80 leading-relaxed max-w-2xl">
                    Der nächste Schritt ist nicht mehr reine Analyse. Jetzt geht es darum,
                    die wirtschaftlich sinnvollste Paketstufe zu wählen, um diese Lecks
                    priorisiert zu schließen.
                  </p>
                </div>

                {/* Empfehlungs-Karte im Hero */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <Card className="border-primary-foreground/20 bg-primary-foreground/10 text-primary-foreground shadow-2xl backdrop-blur-sm">
                    <CardContent className="p-6">
                      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary-foreground/60 mb-2">
                        Empfohlenes Paket
                      </div>
                      <div className="text-2xl font-black text-primary-foreground mb-1">{name}</div>
                      <div className="text-3xl font-black text-accent">{priceLabel}</div>
                      <div className="text-sm text-primary-foreground/70 mt-1">
                        Laufzeit: {durationLabel}
                      </div>
                      {annualLoss > 0 && (
                        <div className="mt-4 rounded-xl border border-primary-foreground/20 bg-primary-foreground/10 px-4 py-3">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-primary-foreground/60 mb-1">
                            Jahresverlustpotenzial
                          </div>
                          <div className="text-xl font-black text-primary-foreground">
                            {formatCurrency(annualLoss)}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── HAUPTINHALT ───────────────────────────────────────────────── */}
        <div className="container mx-auto px-4 py-12 md:py-16 max-w-6xl">
          <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">

            {/* ── LINKS: Begründung + Kosten + Outcome ──────────────────── */}
            <div className="space-y-6">

              {/* 1. Warum dieses Paket */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Card className={`shadow-sm border ${urgencyStyles[urgency]}`}>
                  <CardContent className="p-6 md:p-8">
                    <div className="flex items-center gap-2 mb-4 text-foreground font-bold">
                      <AlertTriangle className="h-5 w-5 shrink-0" />
                      Warum genau dieses Paket empfohlen wird
                    </div>
                    <h3 className="text-2xl font-black text-foreground mb-3 leading-snug">
                      {headline}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      {rationale}
                    </p>
                    <div className="space-y-3">
                      {reasons.map((reason) => (
                        <div key={reason} className="flex items-start gap-3 text-sm">
                          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                          <span className="text-foreground">{reason}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* 2. Kosten des Nicht-Handelns */}
              {(monthlyLoss > 0 || annualLoss > 0) && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <Card className="shadow-sm">
                    <CardContent className="p-6 md:p-8">
                      <div className="flex items-center gap-2 mb-5 font-bold text-foreground">
                        <TrendingDown className="h-5 w-5 text-red-600 shrink-0" />
                        Kosten des Nicht-Handelns
                      </div>
                      <div className="grid gap-4 md:grid-cols-2">
                        {monthlyLoss > 0 && (
                          <div className="rounded-2xl border bg-card p-5">
                            <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-2">
                              Monatlich
                            </div>
                            <div className="text-3xl font-black text-foreground">
                              {formatCurrency(monthlyLoss)}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                              potenzieller Verlust, wenn die aktuellen Lecks bestehen bleiben
                            </p>
                          </div>
                        )}
                        {annualLoss > 0 && (
                          <div className="rounded-2xl border bg-card p-5">
                            <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-2">
                              Jährlich
                            </div>
                            <div className="text-3xl font-black text-foreground">
                              {formatCurrency(annualLoss)}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                              wirtschaftlicher Druck auf Zielerreichung und Pipeline
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="mt-6 space-y-3 text-sm border-t border-border pt-5">
                        <div className="flex items-start gap-3">
                          <Clock3 className="h-4 w-4 mt-0.5 text-red-600 shrink-0" />
                          <span className="text-muted-foreground">
                            Mehr Aktivität ersetzt keine schlechte Conversion.
                          </span>
                        </div>
                        <div className="flex items-start gap-3">
                          <ShieldAlert className="h-4 w-4 mt-0.5 text-red-600 shrink-0" />
                          <span className="text-muted-foreground">
                            Vertagte Optimierung macht spätere Korrekturen teurer.
                          </span>
                        </div>
                        <div className="flex items-start gap-3">
                          <TrendingUp className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                          <span className="text-muted-foreground">
                            Schon kleine Verbesserungen können einen Teil dieses Verlusts zurückholen.
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* 3. Was Sie im nächsten Schritt erhalten */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Card className="shadow-sm">
                  <CardContent className="p-6 md:p-8">
                    <div className="flex items-center gap-2 mb-5 font-bold text-foreground">
                      <Target className="h-5 w-5 text-primary shrink-0" />
                      Was Sie im nächsten Schritt erhalten
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {outcome.map((item) => (
                        <div key={item} className="rounded-xl border bg-card p-4 text-sm">
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                            <span className="text-foreground leading-snug">{item}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-5 rounded-2xl border bg-muted/40 p-5">
                      <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-2">
                        Nächster Schritt
                      </div>
                      <p className="text-sm text-foreground leading-relaxed font-medium">
                        {nextStep}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* ── RECHTS: Sticky Paketkarte + Retainer ──────────────────── */}
            <div className="space-y-5 lg:sticky lg:top-24 h-fit">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="space-y-5"
              >

                {/* ── Hauptkarte: Empfohlenes Paket ─────────────────────── */}
                <Card className="border-2 border-primary/25 shadow-2xl">
                  <CardContent className="p-6 md:p-8">
                    <div className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary mb-4">
                      <Zap className="mr-1.5 h-3 w-3" />
                      Paketempfehlung
                    </div>

                    <div className="text-2xl font-black text-foreground mb-2">{name}</div>
                    <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                      Basierend auf Reifegrad, Umsatzverlust und Funnel-Druck ist das
                      aktuell der wirtschaftlich sinnvollste nächste Schritt.
                    </p>

                    {/* Investition */}
                    <div className="rounded-2xl bg-muted/40 p-5 mb-5">
                      <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-2">
                        Investition
                      </div>
                      <div className="text-4xl font-black text-foreground">{priceLabel}</div>
                      <div className="text-sm text-muted-foreground mt-2">
                        Laufzeit: {durationLabel}
                      </div>
                    </div>

                    {/* Audit-Signale */}
                    <div className="space-y-2.5 mb-6 text-sm">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                        <span className="text-foreground">Empfehlung basiert auf echten Audit-Daten</span>
                      </div>
                      {scoreSignals.overallScore > 0 && (
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                          <span className="text-foreground">
                            Aktueller Reifegrad: {scoreSignals.overallScore.toFixed(0)} / 100
                          </span>
                        </div>
                      )}
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                        <span className="text-foreground">
                          Blockierende Funnel-Stufen: {scoreSignals.blockingStages}
                        </span>
                      </div>
                    </div>

                    {/* CTAs */}
                    <div className="space-y-3">
                      <Button
                        className="w-full h-12 text-base font-semibold rounded-2xl shadow-lg"
                        onClick={() => window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer')}
                      >
                        {ctaLabel}
                        <Calendar className="ml-2 h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full h-12 text-base"
                        onClick={() => window.open(OFFER_EMAIL, '_blank', 'noopener,noreferrer')}
                      >
                        Angebot anfordern
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>

                      {/* Trennlinie */}
                      <div className="flex items-center gap-3 py-1">
                        <div className="h-px flex-1 bg-border" />
                        <span className="text-xs text-muted-foreground">oder</span>
                        <div className="h-px flex-1 bg-border" />
                      </div>

                      {/* Handlungsempfehlung freischalten – 149 € */}
                      <UnlockReportButton navigate={navigate} />
                    </div>
                  </CardContent>
                </Card>

                {/* ── Andere Pakete — klein als Alternative ─────────────── */}
                <div className="space-y-2">
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground px-1 mb-3">
                    Weitere Optionen
                  </div>
                  {allPackages.map((key) => {
                    const pkg = packageMeta[key];
                    return (
                      <button
                        key={key}
                        onClick={() => window.open(OFFER_EMAIL, '_blank', 'noopener,noreferrer')}
                        className="w-full text-left rounded-xl border border-border bg-card hover:bg-muted/40 transition-colors p-4 group"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-xs font-semibold text-foreground truncate">{pkg.name}</div>
                            <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed line-clamp-2">{pkg.description}</div>
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="text-sm font-black text-foreground">{pkg.priceLabel}</div>
                            <div className="text-[10px] text-muted-foreground">{pkg.durationLabel}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  <p className="text-[10px] text-muted-foreground px-1 pt-1 leading-relaxed">
                    Nicht das richtige Paket? Schreiben Sie uns — wir finden gemeinsam die passende Stufe.
                  </p>
                </div>

                {/* ── Zurück ─────────────────────────────────────────────── */}
                <Button
                  variant="ghost"
                  className="w-full text-muted-foreground"
                  onClick={() => navigate(-1)}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Zurück zu den Ergebnissen
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

// ── Inline-Komponente: Handlungsempfehlung für 149 € freischalten ────────────
function UnlockReportButton({ navigate: _navigate }: { navigate: ReturnType<typeof useNavigate> }) {
  const [isPaying, setIsPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUnlock = async () => {
    setIsPaying(true);
    setError(null);

    try {
      const origin = window.location.origin;
      const pathname = window.location.pathname.replace(/\/$/, '') || '';

      // BOMBENSICHERER ANSATZ:
      // 1. Freischaltung JETZT in localStorage speichern (vor Checkout)
      //    → Stripe kann keine Daten löschen, localStorage bleibt erhalten
      try { localStorage.setItem('salesforge_unlocked', 'true'); } catch { /* ignore */ }
      try { sessionStorage.setItem('salesforge_stripe_success', 'true'); } catch { /* ignore */ }

      // 2. success_url zeigt DIREKT auf die Handlungsempfehlung
      //    HashRouter verarbeitet #/handlungsempfehlungen sofort korrekt
      //    KEIN Query-Parameter nötig → KEIN Script-Handling nötig
      const successUrl = `${origin}${pathname}/?stripe_success=true`;
      const cancelUrl  = `${origin}${pathname}/?stripe_cancelled=true`;

      const { data, error: fnError } = await supabase.functions.invoke('create_checkout_session', {
        body: { successUrl, cancelUrl },
      });

      if (fnError) throw new Error(fnError.message);
      if (!data?.url) throw new Error('Keine Checkout-URL erhalten');

      // Weiterleitung zu Stripe Checkout
      window.location.href = data.url;
    } catch (err) {
      console.error('Stripe checkout error:', err);
      // Freischaltung rückgängig machen wenn Checkout fehlschlägt
      try { localStorage.removeItem('salesforge_unlocked'); } catch { /* ignore */ }
      try { sessionStorage.removeItem('salesforge_stripe_success'); } catch { /* ignore */ }
      setError('Zahlung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.');
      setIsPaying(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-muted/30 p-4 space-y-3">
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        Handlungsempfehlung direkt freischalten
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-black text-foreground">149 €</span>
        <span className="text-xs text-muted-foreground">einmalig · sofort verfügbar</span>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">
        Kein Gespräch nötig. Sofort Zugang zum vollständigen, auf Ihre Audit-Daten
        abgestimmten Maßnahmenplan.
      </p>

      {/* Fehler-Hinweis */}
      {error && (
        <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 border border-red-200">
          {error}
        </p>
      )}

      <Button
        variant="outline"
        className="w-full h-11 font-semibold text-sm"
        onClick={handleUnlock}
        disabled={isPaying}
      >
        {isPaying ? (
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 border-2 border-foreground/30 border-t-foreground rounded-full animate-spin" />
            Weiterleitung zu Stripe…
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Lock className="h-4 w-4" />
            Handlungsempfehlung freischalten · 149 €
          </span>
        )}
      </Button>

      {/* Stripe-Badge */}
      <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
        <svg className="h-3.5" viewBox="0 0 60 25" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a10 10 0 01-4.56.93c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.65zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.64l.24 1.08c.57-.71 1.59-1.39 3.21-1.39 3.23 0 5.26 2.86 5.26 7.26 0 5.1-2.16 7.78-5.31 7.78zm-.79-11.34c-.85 0-1.56.38-1.97.86l.05 5.27c.38.43 1.06.88 1.92.88 1.63 0 2.44-1.57 2.44-3.54 0-1.9-.78-3.47-2.44-3.47zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7l4.13-.88v3.36l-4.13.88V.87zm-4.32 9.35v10.22h-4.13V5.57h3.56l.28 1.3c.82-1.1 2.1-1.61 3.44-1.61.28 0 .56.02.81.07v3.79c-.24-.04-.53-.07-.85-.07-1.51 0-2.67.48-3.11 1.17zm-9.58 4.56c0 1.97 1.6 2.59 3.38 2.59.84 0 1.86-.2 2.77-.53v3.32c-1.05.42-2.42.64-3.81.64-3.79 0-6.43-1.97-6.43-6.06V8.63H7.5V5.57h2.75V2.18l4.09-.87v4.26h3.78v3.06h-3.78v4.71z" fill="currentColor"/>
        </svg>
        Sichere Zahlung via Stripe
      </div>

      {/* Paywall-Rechtstext / Microcopy */}
      <p className="text-[11px] text-muted-foreground leading-relaxed pt-1">
        Mit dem Kauf erhalten Sie einmaligen Zugang zur KI-gestützten Handlungsempfehlung
        basierend auf Ihren Audit-Eingaben. Die Inhalte sind automatisiert generiert und
        stellen keine individuelle Unternehmensberatung dar.{' '}
        <strong>Es wird keine Garantie für bestimmte Umsatz- oder Geschäftsergebnisse übernommen.</strong>{' '}
        Das Angebot richtet sich ausschließlich an Unternehmer (§ 14 BGB).
        Kein Widerrufsrecht. Es gelten unsere{' '}
        <a href="#/agb" className="underline hover:text-foreground">AGB</a>{' '}und{' '}
        <a href="#/datenschutz" className="underline hover:text-foreground">Datenschutzerklärung</a>.
      </p>
    </div>
  );
}